import { HAND_BREAK_MS, TRICK_PAUSE_MS, turnMsFor } from "../batak/tempo";
import { isCard, otherHands } from "../core/cards";
import { GameRuleError, } from "../core/contract";
import { ruleToggle } from "../core/rules";
import { at, placesOf } from "../core/util";
import { botPauseFor, chooseCard, choosePass } from "./bot";
import { createGame, startNextHand } from "./hand";
import { applyPass } from "./pass";
import { applyCard, collectTrick, getValidCards } from "./play";
import { currentActor, HeartsError, PASS_COUNT, passDirection, pendingPassers, } from "./state";
import { buildGameView } from "./view";
/** Choosing three cards takes longer than playing one (decision 185). */
export const PASS_TIME_FACTOR = 1.5;
export function passMsFor(tempo, tempoMs) {
    return Math.round(turnMsFor(tempo, "playing", tempoMs) * PASS_TIME_FACTOR);
}
function optionsFrom(settings) {
    const options = {
        jackOfDiamonds: ruleToggle(settings.rules, "jackOfDiamonds", false),
    };
    if (settings.length !== undefined)
        options.length = settings.length;
    if (settings.goal !== undefined)
        options.goal = settings.goal;
    else if (settings.length !== undefined)
        options.goal = null;
    return options;
}
const snapshotOf = (state) => ({
    phase: state.phase,
    heartsBroken: state.heartsBroken,
    queenTaker: state.queenTaker,
});
function handEvents(state) {
    const result = state.lastHandResult;
    if (result === null)
        return [];
    const events = [];
    if (result.moon !== null)
        events.push({ code: "moon-shot", seat: result.moon });
    events.push({
        code: "hand-scored",
        data: Object.fromEntries(result.deltas.map((delta, seat) => [`d${seat}`, delta])),
    });
    return events;
}
/** Events for what a move or an advance changed. */
function transitionEvents(state, before) {
    const events = [];
    if (before.phase === "passing" && state.phase === "playing")
        events.push({ code: "cards-passed", data: { direction: passDirection(state.hand) } });
    if (!before.heartsBroken && state.heartsBroken)
        events.push({ code: "hearts-broken" });
    if (before.queenTaker === null && state.queenTaker !== null)
        events.push({ code: "queen-taken", seat: state.queenTaker });
    const ended = state.phase === "hand-end" || state.phase === "game-end";
    if (before.phase === "playing" && ended)
        events.push(...handEvents(state));
    return events;
}
function parseCards(raw) {
    if (!Array.isArray(raw) || raw.length !== PASS_COUNT || !raw.every(isCard))
        return null;
    return raw.map((card) => ({ suit: card.suit, rank: card.rank }));
}
export const heartsEngine = {
    id: { family: "hearts", variant: "solo" },
    create(seed, settings) {
        return createGame(seed, optionsFrom(settings));
    },
    actor(state) {
        return currentActor(state);
    },
    parseMove(raw) {
        if (typeof raw !== "object" || raw === null)
            return null;
        const move = raw;
        if (move.kind === "pass") {
            const cards = parseCards(move.cards);
            return cards === null ? null : { kind: "pass", cards };
        }
        if (move.kind === "play")
            return isCard(move.card) ? { kind: "play", card: move.card } : null;
        return null;
    },
    apply(state, seat, move) {
        const before = snapshotOf(state);
        try {
            if (move.kind === "pass")
                applyPass(state, seat, move.cards);
            else
                applyCard(state, seat, move.card);
        }
        catch (error) {
            throw new GameRuleError(error instanceof HeartsError ? error.code : "move-rejected");
        }
        return transitionEvents(state, before);
    },
    autoMove(state, seat, level) {
        if (state.phase === "passing")
            return { kind: "pass", cards: choosePass(state, seat, level) };
        return { kind: "play", card: chooseCard(state, seat, level) };
    },
    fallbackMove(state, seat) {
        if (state.phase === "passing")
            return { kind: "pass", cards: at(state.hands, seat).slice(0, PASS_COUNT) };
        const valid = getValidCards(state, seat);
        if (valid.length === 0)
            throw new GameRuleError("no-card-to-play");
        return { kind: "play", card: at(valid, 0) };
    },
    pacing(state, settings) {
        if (state.phase === "game-end")
            return { kind: "over" };
        if (state.phase === "hand-end")
            return { kind: "pause", ms: HAND_BREAK_MS, boundary: "round" };
        if (state.phase === "passing") {
            // Everyone chooses at once on one clock; the hand number names the wait (ADR 0046).
            const ms = passMsFor(settings.tempo, settings.tempoMs);
            return { kind: "actors", seats: pendingPassers(state), ms, turn: state.hand };
        }
        if (state.trick.winner !== null)
            return { kind: "pause", ms: TRICK_PAUSE_MS, boundary: "step" };
        const seat = currentActor(state);
        if (seat === null)
            return { kind: "idle" };
        return { kind: "actor", seat, ms: turnMsFor(settings.tempo, "playing", settings.tempoMs) };
    },
    botPause(state, seat) {
        return botPauseFor(state, seat);
    },
    advance(state) {
        if (state.phase === "playing" && state.trick.winner !== null) {
            const before = snapshotOf(state);
            collectTrick(state);
            return transitionEvents(state, before);
        }
        if (state.phase === "hand-end") {
            startNextHand(state);
            return [
                { code: "hand-dealt", data: { hand: state.hand, direction: passDirection(state.hand) } },
            ];
        }
        return [];
    },
    view(state, seat) {
        return buildGameView(state, seat);
    },
    concealed(state, seat) {
        return otherHands(state.hands, seat);
    },
    result(state) {
        if (state.phase !== "game-end")
            return null;
        return {
            scores: [...state.scores],
            winners: [...(state.winners ?? [])],
            places: placesOf(state.scores, "low"),
        };
    },
    lastRoundRecord(state) {
        const result = state.lastHandResult;
        if (!result)
            return null;
        return { round: result.hand, payload: result, seatCounters: [...result.points] };
    },
};
//# sourceMappingURL=engine.js.map